import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { ReactNode } from 'react';

import { Spacing, Typography } from 'chatie-design-system';

import Button from '@components/common/Button';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { useWebView } from '@utils/webview';

const containerCSS = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${Spacing.xlarge}px;
  text-align: center;
  gap: ${Spacing.medium}px;
`;

const emojiCSS = css`
  font-size: 48px;
`;

const titleCSS = css`
  ${Typography.h5}
`;

const descCSS = css`
  ${Typography.body14}
  white-space: pre-line;
`;

type Props = {
  needsLogin: boolean;
  needsCertification: boolean;
  isLoading: boolean;
  children: ReactNode;
};

const AdultContentBlocker = ({
  needsLogin,
  needsCertification,
  isLoading,
  children,
}: Props) => {
  const { t } = useTranslation();
  const { platform } = useGlobalContext();
  const { web } = useWebView();

  if (isLoading) {
    return null;
  }

  if (!needsLogin && !needsCertification) {
    return <>{children}</>;
  }

  const handleLogin = () => {
    if (platform === Platform.web) {
      window.location.href = '/signin';
    } else {
      window.parent.postMessage({ type: 'NotAuthenticated' }, '*');
    }
  };

  const handleCertification = () => {
    if (platform === Platform.web) {
      // 웹에서는 앱 다운로드 유도 또는 인증 페이지로
      web.openMarket();
    } else {
      window.parent.postMessage({ type: 'AC' }, '*');
    }
  };

  return (
    <div css={containerCSS}>
      <span css={emojiCSS}>🔞</span>
      <p css={titleCSS}>
        {needsLogin
          ? t('로그인이 필요해요')
          : t('성인인증이 필요해요')}
      </p>
      <p css={descCSS}>
        {needsLogin
          ? t('19세 이상만 이용 가능한 콘텐츠입니다.\n로그인 후 이용해 주세요.')
          : t('19세 이상만 이용 가능한 콘텐츠입니다.\n성인인증 후 이용해 주세요.')}
      </p>
      <Button
        onClick={needsLogin ? handleLogin : handleCertification}
      >
        {needsLogin ? t('로그인') : t('성인인증 하기')}
      </Button>
    </div>
  );
};

export default AdultContentBlocker;
